import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { colors, gradients, spacing } from '../../constants/theme';
import { getSSOProvider } from '../../config/authorizedDomains';
import logoUrl from '../../assets/intuition_ai_logo.png';

const LoginContainer = styled.div`
  min-height: 100vh;
  background: ${gradients.background};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
`;

const LoginCard = styled.div`
  background: ${colors.background.paper};
  border-radius: 16px;
  padding: ${spacing.xxl};
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  border: 1px solid ${colors.background.tertiary};
  max-width: 400px;
  width: 100%;
  text-align: center;
`;

const Logo = styled.img`
  width: 120px;
  height: auto;
  margin-bottom: ${spacing.xl};
`;

const Title = styled.h1`
  color: ${colors.text.primary};
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: ${spacing.sm};
  background: ${gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: ${colors.text.secondary};
  font-size: 1rem;
  margin-bottom: ${spacing.xl};
`;

const SSOButton = styled.button`
  background: ${gradients.primary};
  color: ${colors.primary.contrast};
  border: none;
  border-radius: 12px;
  padding: ${spacing.md} ${spacing.xl};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  width: 100%;
  min-height: 50px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(99, 102, 241, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SSOIcon = styled.svg`
  width: 20px;
  height: 20px;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: ${spacing.md};
  border: 2px solid ${colors.background.tertiary};
  border-radius: 8px;
  font-size: 1rem;
  background: ${colors.background.secondary};
  color: ${colors.text.primary};
  margin-bottom: ${spacing.md};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 2px ${colors.primary.main}20;
  }

  &::placeholder {
    color: ${colors.text.secondary};
  }
`;

const ErrorMessage = styled.div`
  color: ${colors.error};
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  padding: ${spacing.md};
  margin-top: ${spacing.md};
  font-size: 0.9rem;
`;

const LoginPage: React.FC = () => {
  const { signInWithEmail, error: authError, clearError, dashboardLoading, loadingStep } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Combined error from auth context or local state
  const error = authError || localError;

  // Combined loading state
  const isLoading = loading || dashboardLoading;

  // Get provider for button text display
  const getButtonText = (): string => {
    if (dashboardLoading) {
      const steps = [
        'Authenticating your account...',
        'Loading your company data...',
        'Preparing your dashboard...'
      ];
      if (loadingStep < 1 || loadingStep > steps.length) {
        return 'Loading your Dashboard...';
      }
      return steps[loadingStep - 1];
    }
    if (loading) return 'Signing in...';
    if (!email.includes('@')) return 'Continue with SSO';
    
    try {
      const provider = getSSOProvider(email);
      return `Continue with ${provider === 'microsoft' ? 'Microsoft' : 'Google'}`;
    } catch {
      return 'Continue with SSO';
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear errors when user starts typing
    if (error) {
      setLocalError(null);
      clearError();
    }
  };

  const handleSignIn = async () => {
    if (!email.includes('@')) {
      setLocalError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setLocalError(null);
    clearError();
    
    try {
      await signInWithEmail(email);
    } catch (err: any) {
      let errorMessage = err.message || 'Failed to sign in';
      
      // Handle specific Firebase auth errors
      if (err.message?.includes('auth/unauthorized-domain')) {
        errorMessage = 'Domain not authorized. Please add "localhost" to your Firebase Auth authorized domains in the Firebase Console.';
      }
      
      setLocalError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSignIn();
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Logo src={logoUrl} alt="Intuition AI Logo" />
        <Title>Welcome to Intuition AI</Title>
        <Subtitle>Enter your work email to continue</Subtitle>
        
        <EmailInput
          type="email"
          placeholder="Enter your work email address"
          value={email}
          onChange={handleEmailChange}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />

        <SSOButton onClick={handleSignIn} disabled={!email.includes('@') || isLoading}>
          <SSOIcon viewBox="0 0 24 24" fill="currentColor">
            {/* Shield with lock icon - represents secure SSO authentication */}
            <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11H16V16H8V11H9.2V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.4,8.7 10.4,10V11H13.6V10C13.6,8.7 12.8,8.2 12,8.2Z" />
          </SSOIcon>
          {getButtonText()}
        </SSOButton>

        {error && (
          <ErrorMessage>
            {error.includes('domain') ? (
              <div>
                💡 <strong>For authorized access:</strong> Please use your company email address or contact your administrator to add your domain to the authorized list.
              </div>
            ) : (
              error
            )}
          </ErrorMessage>
        )}
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;